/**
 * The shell's own English strings — only the words a shell part says by
 * itself (a close button's name, the "⋯" button's name, a back chevron's
 * name, what a tab's badge means to a screen reader). Anything a game wants a
 * part to say arrives as a prop, already translated by the game.
 *
 * `{placeholder}` tokens are filled in by `shellText` — see `i18n.svelte.ts`.
 */
export const shellEn = {
  close: 'Close',
  moreOptions: 'More options',
  back: 'Back',
  navigation: 'Sections',
  newItems: '{count} new',
  dismiss: 'Dismiss',
  clear: 'Clear',
  // The search suggestions (SearchSuggest, TwinTen ADR 0065).
  searchMinLength: 'Type at least {count} characters to search.',
  searching: 'Searching…',
  searchResultsOne: '1 suggestion',
  searchResultsMany: '{count} suggestions',
  searchFailed: 'The search did not work. Try again.',
  // The way out of a full page of grouped rows (FullPage).
  done: 'Done',
  // The templates' own words (1.2.0), each a prop's default.
  gameMenu: 'Game menu', // GameTopBar's menu button (menuLabel)
  seatYou: 'You', // SeatRow (youLabel)
  seatBot: 'Bot', // SeatRow (botLabel)
  seatReady: 'Ready', // SeatRow (readyLabel)
  seatNotReady: 'Not ready', // SeatRow (notReadyLabel)
  paste: 'Paste', // JoinTemplate (pasteLabel)
  build: 'Build {id}', // SettingsPage's footer (formatBuild)
  // Home's invites (polish lane, 1.2.0).
  invitesMore: '+{count} more', // HomeTemplate's invites (formatMore)
  // Matchmaking (1.2.0).
  searchElapsed: 'Time searching', // SearchingTemplate's clock (elapsedLabel)
  // The accent lane (1.2.0): AccentPicker's words, each a prop's default.
  accentLabel: 'Accent colour', // label
  accentCustom: 'Custom', // customLabel
  accentHue: 'Hue', // hueLabel
  accentVividness: 'Vividness', // vividnessLabel
  accentAdjusted: 'Adjusted so it stays readable on this background.', // adjustedNote
  accentPreviewAction: 'Play', // previewAction
  accentPreviewToggle: 'On', // previewToggle
  // The hue slider's spoken colour (hueName), by hueFamily.
  huePink: 'pink',
  hueRed: 'red',
  hueOrange: 'orange',
  hueYellow: 'yellow',
  hueGreen: 'green',
  hueTeal: 'teal',
  hueBlue: 'blue',
  huePurple: 'purple',
  // The profile (TwinTen ADR 0073): ProfileEditor's and PlayerSheet's defaults.
  profileEdit: 'Edit profile', // ProfileEditor (title)
  profilePicture: 'Profile picture', // ProfileEditor (pictureLabel)
  profileAddPicture: 'Add a picture', // ProfileEditor (addPictureLabel)
  profileChangePicture: 'Change picture', // ProfileEditor (changePictureLabel)
  profileRemovePicture: 'Remove', // ProfileEditor (removePictureLabel)
  profileSlogan: 'Slogan', // ProfileEditor (sloganLabel)
  profileCount: '{count}/{max}', // ProfileEditor (formatCount)
  profileSex: 'Sex', // ProfileEditor (sexLabel)
  profileBirthYear: 'Birth year', // ProfileEditor (birthYearLabel)
  profileNotShown: 'Not shown', // ProfileEditor (notShownLabel)
  profileNote: 'Friends see all of this. Everyone else sees your name and picture.',
  profileLoading: 'Loading…', // PlayerSheet (loadingText)
  // The lobby's code while it is on its way (1.4.0).
  lobbyCodePending: 'Waiting for the code…', // LobbyCode (pendingLabel)
  // 1.5.0 (shell-150 lane): ProfileEditor's own word for sex.
  profileSexOther: 'In your own words', // ProfileEditor (sexOtherLabel)
  // 1.7.0: playing together on one device (HandOff, Standings, PlayerNames).
  handOffTitle: 'Over to {name}', // HandOff (title)
  handOffReady: 'I am {name}', // HandOff (readyLabel)
  standingsWinner: 'Winner', // Standings (winnerLabel)
  standingsRank: 'Rank {rank}', // Standings, for screen readers (formatRank)
  standingsTied: 'Tied for rank {rank}', // Standings, for screen readers (formatRank)
  playerNamesLabel: 'Players', // PlayerNames (label)
  playerNamesDefault: 'Player {n}', // PlayerNames (defaultName)
  playerNamesAdd: 'Add a player', // PlayerNames (addLabel)
  playerNamesRemove: 'Remove {name}', // PlayerNames (removeLabel)
  playerNamesDuplicate: 'Another player already has this name.', // PlayerNames (duplicateMessage)
  // 1.8.0: a graded choice, for screen readers.
  choiceRight: 'Right answer', // ChoiceCard / RadioCards tone right (toneLabels.right)
  choiceWrong: 'Wrong answer', // ChoiceCard / RadioCards tone wrong (toneLabels.wrong)
  // 1.9.0: GameFrame's two pad sets, each a named group.
  gamePads: 'Player {n} controls', // GameFrame (padsLabels)
} as const;

export type ShellStringKey = keyof typeof shellEn;
